import React, {useCallback, useMemo, useState, ReactNode} from "react";
import {Formik} from "formik";
import {Prompt} from "react-router-dom";
import {RoleNameEnum} from "../../../../utils/types/common";
import {
  Template,
  getValidationConfig,
  extractTemplateSlides,
} from "@reside/forms";
import {
  BlockField,
  QueryVariableMap,
  BlockComponentContextProvider,
  FormikValidateAllOnSubmit,
  FormikAutoSaveValues,
  ThinScrollbar,
  ScrollToError,
} from "@reside/ui";

import {transComponents} from "../../../../atoms/block-components";

import {
  FacilitySettingsVariables,
  FieldArrayItem,
} from "../../../../services/FacilitySettingsService";
import {FormikReinitializePcc} from "../../hooks/useFormikReinitializePcc";
import {AdminRoles, NotResideAdmin} from "../../../../atoms/admin-roles";
import {FacilityPccMappingForm} from "./forms/FacilityPccMappingForm";
import {
  EditFacilityConfirmationModal,
  EditFacilityModalProvider,
} from "../../../../atoms/edit-facility-confirmation-modal";
import {EditFacilityForm} from "./forms/EditFacilityForm";
import {DownloadTemplateForm} from "./forms/DownloadTemplateForm";
import {StyledForm, StyledScrollableErrorArea} from "./styles";
import {getChecklistWithNormalizedIds} from "../../utils";
import {
  FACILITY_GO_LIVE_DATE_BLOCK,
  INCLUDE_IN_AUDIT_LOG_BLOCK,
} from "./consts";
import {withAccessToken} from "../../../../utils/url";
import {getCommonAuthorizationHeader} from "../../../../services/api/selectors";

export type Props = Readonly<{
  header: ReactNode;
  template: Template;
  templateNames: string[];
  queryVariables?: QueryVariableMap;
  /**
   * Initial Form values for the template.
   */
  initialValues: FacilitySettingsVariables;
  isPccEnabled?: boolean;
  onSubmit?: (
    values: FacilitySettingsVariables,
  ) => Promise<FacilitySettingsVariables>;
}>;

export const FacilitySettingsForm = ({
  header,
  template,
  initialValues,
  onSubmit,
  queryVariables,
  isPccEnabled,
}: Props) => {
  const [values, setValues] =
    useState<FacilitySettingsVariables>(initialValues);

  const [{children: blocks}] = useMemo(() => {
    const extractedBlocks = extractTemplateSlides(template, values as any);

    //@ts-expect-error - children exist
    extractedBlocks[0]?.children[0]?.children.splice(2, 0, {
      ...FACILITY_GO_LIVE_DATE_BLOCK,
      state: {
        ...FACILITY_GO_LIVE_DATE_BLOCK.state,
        disabled: initialValues.userRole !== RoleNameEnum.RESIDE_ADMIN,
      },
    }); // Inject go live date block after facility name block manually because it is not part of the template.

    if (initialValues.userRole === RoleNameEnum.RESIDE_ADMIN) {
      const enrich = (node: any): boolean => {
        if (!node) return false;
        if (node.id === "checklist" && Array.isArray(node.children)) {
          node.children = [
            ...node.children,
            INCLUDE_IN_AUDIT_LOG_BLOCK,
          ] as typeof node.children;
          return true;
        }
        const kids = [...(Array.isArray(node.children) ? node.children : [])];
        for (const k of kids) if (enrich(k)) return true;
        return false;
      };

      for (const block of extractedBlocks) {
        if (enrich(block)) break;
      }
    }

    return extractedBlocks;
  }, [values, template, initialValues.userRole]);

  const {validate, validationRules} = useMemo(
    () =>
      getValidationConfig({
        children: blocks,
        answers: values,
      }),
    [blocks, values],
  );

  // TODO(RESIDE-3488): Support relative rules instead of this
  const estimatedMaxCostForSNFABN = (
    (validationRules.estimatedMaxCostForSNFABN || []) as ReadonlyArray<string>
  ).map(rule =>
    rule === "min_value:0"
      ? `min_value:${values.estimatedCostForSNFABN}`
      : rule,
  );

  const validateForm = useCallback(
    values =>
      validate(values, {
        estimatedMaxCostForSNFABN,
        facilityName: ["required", "facility-name"],
        facilityGoLiveDate:
          initialValues.facilityGoLiveDate === ""
            ? FACILITY_GO_LIVE_DATE_BLOCK.rules
            : [...FACILITY_GO_LIVE_DATE_BLOCK.rules, "required"], // Required only if go live date is set, to prevent empty date from being saved
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validate, estimatedMaxCostForSNFABN, initialValues.facilityGoLiveDate],
  );

  return (
    <EditFacilityModalProvider>
      <EditFacilityConfirmationModal />
      <Formik<FacilitySettingsVariables>
        initialValues={values}
        validate={validateForm}
        onSubmit={async (values, formik) => {
          formik.setSubmitting(true);

          const {checklist, isPccEnabled, userRole, ...rest} = values;

          const savedValues = await onSubmit({
            checklist: getChecklistWithNormalizedIds(
              checklist as ReadonlyArray<FieldArrayItem>,
            ),
            ...rest,
            isPccEnabled,
            userRole,
          });

          formik.setSubmitting(false);

          if (savedValues) {
            initialValues.facilityGoLiveDate = savedValues.facilityGoLiveDate;
            formik.resetForm({
              values: {...savedValues, isPccEnabled, userRole},
            });
          }
        }}
      >
        {formik => (
          <StyledForm>
            <FormikValidateAllOnSubmit />
            <FormikReinitializePcc isPccEnabled={isPccEnabled} />
            <FormikAutoSaveValues disableDebounce onChangeValues={setValues} />
            <Prompt
              when={formik.dirty}
              message="After leaving all of your unsaved changes will be lost."
            />
            <ScrollToError offsetTop={200} />
            {header}

            <ThinScrollbar>
              <StyledScrollableErrorArea>
                <BlockComponentContextProvider value={transComponents}>
                  {blocks.map(block => (
                    <BlockField
                      key={block.id}
                      block={block}
                      queryVariables={{
                        ...queryVariables,
                        withAccessToken,
                        getCommonAuthorizationHeader,
                      }}
                    />
                  ))}
                  <AdminRoles roles={[RoleNameEnum.RESIDE_ADMIN]}>
                    <EditFacilityForm templateName={template.templateName} />
                    <FacilityPccMappingForm isPccEnabled={isPccEnabled} />
                  </AdminRoles>

                  <NotResideAdmin>
                    <DownloadTemplateForm
                      facilityTemplate={template.templateName}
                    />
                  </NotResideAdmin>
                </BlockComponentContextProvider>
              </StyledScrollableErrorArea>
            </ThinScrollbar>
          </StyledForm>
        )}
      </Formik>
    </EditFacilityModalProvider>
  );
};
